import React, { useState } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { Id } from "../../../../../convex/_generated/dataModel";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ExternalLink, CheckCircle, Clock, Trash2, Loader2, Send, FileText } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

interface InteractiveProposalsTabProps {
  bidId: Id<"bids">;
}

export function InteractiveProposalsTab({ bidId }: InteractiveProposalsTabProps) {
  // Use getAllByBidId to get all versions of the proposal
  const proposals = useQuery(api.bidProposals.getAllByBidId, { bidId });
  const sendEmail = useMutation(api.proposals.sendProposalEmail);

  const [isSending, setIsSending] = useState(false);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "accepted":
        return <Badge className="bg-green-100 text-green-800">Approved</Badge>;
      case "sent":
        return <Badge className="bg-blue-100 text-blue-800">Sent to Client</Badge>;
      case "draft":
        return <Badge variant="secondary">Draft</Badge>;
      case "rejected":
        return <Badge className="bg-red-100 text-red-800">Rejected</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const handleSendEmail = async (proposalId: Id<"bidProposals">) => {
    setIsSending(true);
    try {
      await sendEmail({ proposalId });
      toast.success("Proposal sent to client successfully!");
    } catch (error: any) {
      console.error("Failed to send proposal:", error);
      toast.error(error.message || "Failed to send proposal.");
    } finally {
      setIsSending(false);
    }
  };

  if (proposals === undefined) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary"></div>
      </div>
    );
  }

  // Only show proposals that actually have htmlContent
  const validProposals = proposals.filter((p) => p.htmlContent);

  if (validProposals.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 py-12 text-center">
        <FileText className="mx-auto mb-4 h-12 w-12 text-gray-400" />
        <h3 className="text-lg font-medium text-gray-900">No proposals generated yet</h3>
        <p className="mb-6 mt-1 text-gray-500">
          Create a new interactive proposal using the "Proposal Settings" button above.
        </p>
      </div>
    );
  }


  return (
    <div className="mb-8 space-y-4">
      <h3 className="text-lg font-medium">Interactive Proposals</h3>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Version</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Last Updated</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {validProposals.map((proposal) => {
              const shareUrl = proposal.shareToken
                ? `${window.location.origin}/p/${proposal.shareToken}`
                : null;
                
              return (
                <TableRow key={proposal._id}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      <ExternalLink className="h-4 w-4 text-indigo-600" />
                      Proposal v{proposal.version || 1}
                    </div>
                  </TableCell>
                  <TableCell>{getStatusBadge(proposal.status)}</TableCell>
                  <TableCell className="text-gray-500">
                    <div className="flex items-center gap-1 text-xs">
                      <Clock className="h-3 w-3" />
                      {formatDistanceToNow(proposal.updatedAt || proposal.createdAt || Date.now(), {
                        addSuffix: true,
                      })}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      {shareUrl && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.open(shareUrl, "_blank")}
                          >
                            <ExternalLink className="mr-2 h-4 w-4" />
                            View Public Link
                          </Button>
                          <Button
                            variant="default"
                            size="sm"
                            onClick={() => handleSendEmail(proposal._id)}
                            disabled={isSending || proposal.status === "accepted"}
                          >
                            {isSending ? (
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                              <Send className="mr-2 h-4 w-4" />
                            )}
                            Email Client
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
